import {genUUID} from '../../lib/picos/uuids';

/**
 * Refund keys (OWA-1187). The server refunds once per key, so every retry of one refund must send
 * the same key, and a new refund needs a new one.
 *
 * A submission's key is kept until the server answers it with success: clicking Refund or Void
 * again after an error, reopening the payment, or reloading the page all retry with the original
 * key. It lives in sessionStorage, so it survives a reload but not the browser tab. When storage
 * is unavailable (private mode, a full quota) the in-memory copy still covers retries without a
 * reload, so a storage failure never stops a refund.
 */
export type RefundSubmission =
    | {firmId: string, action: 'refund', transactionId: string, amount: number}
    | {firmId: string, action: 'void', transactionId: string};

const memory = new Map<string, string>();

function storageKey(submission: RefundSubmission): string {
    const {firmId, action, transactionId} = submission;
    const amount = submission.action === 'refund' ? `:${submission.amount}` : '';
    return `outlaw.payments.refundKey:${firmId}:${action}:${transactionId}${amount}`;
}

function readStored(name: string): string | null {
    try {
        return sessionStorage.getItem(name);
    }
    catch {
        return null;
    }
}

/** The key for this submission: the one already pending, or a new one. */
export function refundKeyFor(submission: RefundSubmission): string {
    const name = storageKey(submission);
    const key = readStored(name) || memory.get(name) || genUUID();
    memory.set(name, key);
    try {
        sessionStorage.setItem(name, key);
    }
    catch {
        // The in-memory copy still covers retries in this page.
    }
    return key;
}

/** The server answered this submission with success, so the next one is a new refund. */
export function refundSucceeded(submission: RefundSubmission): void {
    const name = storageKey(submission);
    memory.delete(name);
    try {
        sessionStorage.removeItem(name);
    }
    catch {
        // Storage is unavailable; the refund itself has succeeded.
    }
}
